// Org Inquiries admin tab — shows partnership form submissions from the
// /organization-inquiry page. Lets the user view full submission detail,
// change status, save admin notes, and fire the org-nurture drip on demand.
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Building2, Mail, Phone, Calendar, Send, Save, RefreshCw,
  GraduationCap,
} from "lucide-react";
import { toast } from "sonner";
import { adminFetch, adminPost, adminPatch } from "@/lib/api";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in_conversation", label: "In conversation" },
  { value: "qualified", label: "Qualified" },
  { value: "closed", label: "Closed / Not a fit" },
];

const STATUS_STYLE = {
  new: "border-brand/40 text-brand bg-brand-soft",
  in_conversation: "border-amber-300 text-amber-800 bg-amber-50",
  qualified: "border-emerald-300 text-emerald-700 bg-emerald-50",
  closed: "border-[var(--line)] text-ink/55 bg-bone",
};

// Roles that can appear inside a [STUDENT CAMP INQUIRY · ROLE] tag on
// the additional_comments field. Keep in sync with StudentCamp.jsx.
const STUDENT_CAMP_ROLES = [
  { value: "school",  label: "School" },
  { value: "parent",  label: "Parent" },
  { value: "partner", label: "Partner" },
  { value: "sponsor", label: "Sponsor" },
  { value: "mentor",  label: "Mentor" },
  { value: "general", label: "General" },
];

// Extract the role from an org-inquiry's additional_comments / message.
// Returns lowercase role string (school/parent/…) or null.
function extractStudentCampRole(inq) {
  const text = `${inq?.additional_comments || ""} ${inq?.message || ""} ${inq?.comments || ""}`;
  const m = text.match(/\[STUDENT CAMP INQUIRY(?:\s*·\s*([A-Z]+))?\]/i);
  if (!m) return null;
  return (m[1] || "general").toLowerCase();
}

export default function OrgInquiries({ pw, emailUsage }) {
  const [loading, setLoading] = useState(true);
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [studentCampOnly, setStudentCampOnly] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all"); // "all" | one of STUDENT_CAMP_ROLES.value
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState({ status: "", admin_notes: "" });
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const path = filter === "all" ? "/admin/org-inquiries" : `/admin/org-inquiries?status=${filter}`;
      const data = await adminFetch(path, pw);
      setInquiries(data.inquiries || []);
    } catch {
      toast.error("Failed to load org inquiries");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  // Attach extracted student-camp role to each inquiry once, so filters
  // + table cell rendering stay cheap.
  const decorated = useMemo(
    () => inquiries.map((i) => ({ ...i, _sc_role: extractStudentCampRole(i) })),
    [inquiries]
  );

  // Apply the client-side "Student Camp only" + role chip filters.
  const filteredInquiries = useMemo(() => {
    return decorated.filter((i) => {
      if (studentCampOnly && !i._sc_role) return false;
      if (roleFilter !== "all") {
        if (i._sc_role !== roleFilter) return false;
      }
      return true;
    });
  }, [decorated, studentCampOnly, roleFilter]);

  const counts = useMemo(() => {
    const c = { all: filteredInquiries.length, new: 0, in_conversation: 0, qualified: 0, closed: 0 };
    for (const i of filteredInquiries) {
      const s = i.status || "new";
      if (s in c) c[s] += 1;
    }
    return c;
  }, [filteredInquiries]);

  // Per-role counts across ALL decorated inquiries (independent of the
  // status tab) — powers the numeric badges on each role chip.
  const roleCounts = useMemo(() => {
    const base = { all: 0, school: 0, parent: 0, partner: 0, sponsor: 0, mentor: 0, general: 0 };
    for (const i of decorated) {
      if (studentCampOnly && !i._sc_role) continue;
      base.all += 1;
      if (i._sc_role && i._sc_role in base) base[i._sc_role] += 1;
    }
    return base;
  }, [decorated, studentCampOnly]);

  const studentCampTotal = useMemo(
    () => decorated.filter((i) => i._sc_role).length,
    [decorated]
  );

  const openDetail = (inq) => {
    setSelected(inq);
    setEditing({ status: inq.status || "new", admin_notes: inq.admin_notes || "" });
  };

  const saveDetail = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await adminPatch(`/admin/org-inquiries/${selected.id}`, pw, {
        status: editing.status,
        admin_notes: editing.admin_notes,
      });
      toast.success("Saved.");
      await load();
      setSelected(null);
    } catch (e) {
      toast.error(e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const runOrgNurture = async () => {
    if ((emailUsage?.percent_used ?? 0) >= 90) {
      toast.error(`Resend usage is at ${emailUsage.percent_used}% — bulk sends paused.`);
      return;
    }
    const ok = window.confirm(
      "Fire the organizational follow-up drip now?\n\n" +
        "• Sends the next due follow-up email to every active org inquiry whose timer has elapsed.\n" +
        "• Up to 20 inquiries per click.\n" +
        "• Inquiries marked Qualified or Closed are skipped.\n\n" +
        "Cannot be undone."
    );
    if (!ok) return;
    setRunning(true);
    try {
      await adminPost("/admin/org-nurture/run", pw);
      toast.success("Org follow-up drip fired.");
      await load();
    } catch (e) {
      toast.error(e?.message || "Failed to fire drip");
    } finally {
      setRunning(false);
    }
  };

  if (loading && inquiries.length === 0) {
    return <div className="py-16 text-center text-ink/50"><Loader2 className="inline-block mr-2 animate-spin" size={14} /> Loading…</div>;
  }

  return (
    <div className="space-y-5" data-testid="org-inquiries-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow mb-1">Organizational Partnerships</div>
          <h2 className="font-display text-2xl tracking-[-0.025em]">Inquiries from /organization-inquiry</h2>
          <p className="text-sm text-ink/60 mt-1">
            Every submission of the &ldquo;Bring the AI Build Lab to your organization&rdquo; form lands here. They enter the 4-email follow-up drip automatically.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={load}
            data-testid="org-inq-refresh"
            className="btn-outline-ink rounded-full h-10 px-4"
          >
            <RefreshCw size={13} className="mr-1.5" /> Refresh
          </Button>
          <Button
            variant="outline"
            onClick={runOrgNurture}
            disabled={running || (emailUsage?.percent_used ?? 0) >= 90}
            data-testid="org-inq-run-drip"
            title={(emailUsage?.percent_used ?? 0) >= 90 ? `Paused — Resend at ${emailUsage.percent_used}%` : ""}
            className="btn-outline-ink rounded-full h-10 px-4 border-brand/40 text-brand hover:bg-brand-soft disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {running ? <Loader2 className="animate-spin" size={13} /> : <><Send size={13} className="mr-1.5" /> Run org follow-up now</>}
          </Button>
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-bone border border-[var(--line)]">
          <TabsTrigger value="all" data-testid="org-inq-filter-all">All ({counts.all})</TabsTrigger>
          <TabsTrigger value="new" data-testid="org-inq-filter-new">New ({counts.new})</TabsTrigger>
          <TabsTrigger value="in_conversation" data-testid="org-inq-filter-in-convo">In convo ({counts.in_conversation})</TabsTrigger>
          <TabsTrigger value="qualified" data-testid="org-inq-filter-qualified">Qualified ({counts.qualified})</TabsTrigger>
          <TabsTrigger value="closed" data-testid="org-inq-filter-closed">Closed ({counts.closed})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Student-camp scope toggle + role chips */}
      <div className="rounded-2xl border border-[var(--line)] bg-bone p-3 sm:p-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setStudentCampOnly((s) => !s)}
          data-testid="org-inq-student-camp-toggle"
          className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${
            studentCampOnly
              ? "bg-brand text-white border-brand"
              : "bg-white text-ink border-[var(--line)] hover:bg-brand-soft"
          }`}
        >
          <GraduationCap size={13} />
          Student Camp only
          <span className={`ml-1 text-[10px] font-bold ${studentCampOnly ? "text-white/80" : "text-ink/50"}`}>
            ({studentCampTotal})
          </span>
        </button>

        <span className="text-xs text-ink/45">·</span>

        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/50">Role</span>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setRoleFilter("all")}
            data-testid="org-inq-role-all"
            className={`h-7 px-3 rounded-full text-[11px] font-semibold border transition-colors ${
              roleFilter === "all"
                ? "bg-ink text-white border-ink"
                : "bg-white text-ink/70 border-[var(--line)] hover:bg-white"
            }`}
          >
            All roles ({roleCounts.all})
          </button>
          {STUDENT_CAMP_ROLES.map((r) => {
            const active = roleFilter === r.value;
            const disabled = roleCounts[r.value] === 0;
            return (
              <button
                key={r.value}
                type="button"
                disabled={disabled}
                onClick={() => setRoleFilter(active ? "all" : r.value)}
                data-testid={`org-inq-role-${r.value}`}
                className={`h-7 px-3 rounded-full text-[11px] font-semibold border transition-colors ${
                  active
                    ? "bg-brand text-white border-brand"
                    : disabled
                      ? "bg-white text-ink/30 border-[var(--line)] cursor-not-allowed"
                      : "bg-white text-ink/70 border-[var(--line)] hover:bg-brand-soft hover:text-brand"
                }`}
              >
                {r.label} ({roleCounts[r.value] || 0})
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-[var(--line)] overflow-hidden bg-bone overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[var(--line)] hover:bg-transparent">
              <TableHead className="text-ink/60">Organization</TableHead>
              <TableHead className="text-ink/60">Contact</TableHead>
              <TableHead className="text-ink/60">Audience</TableHead>
              <TableHead className="text-ink/60">Interest</TableHead>
              <TableHead className="text-ink/60">Status</TableHead>
              <TableHead className="text-ink/60">Follow-up</TableHead>
              <TableHead className="text-ink/60">Received</TableHead>
              <TableHead className="text-ink/60 text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredInquiries.length === 0 && (
              <TableRow className="border-[var(--line)] hover:bg-transparent">
                <TableCell colSpan={8} className="text-center text-ink/50 py-10">
                  {studentCampOnly || roleFilter !== "all"
                    ? "No inquiries match this filter. Try clearing the role or Student Camp toggle."
                    : "No inquiries in this view. When someone fills the org partnership form they'll appear here."}
                </TableCell>
              </TableRow>
            )}
            {filteredInquiries.map((inq) => (
              <TableRow
                key={inq.id}
                data-testid={`org-inq-row-${inq.id}`}
                className="border-[var(--line)] hover:bg-white cursor-pointer"
                onClick={() => openDetail(inq)}
              >
                <TableCell className="font-medium text-ink">
                  <div className="flex items-center gap-2">
                    <Building2 size={13} className="text-ink/40" />
                    <div className="flex flex-col">
                      <span>{inq.organization_name || "—"}</span>
                      {inq._sc_role && (
                        <span
                          data-testid={`org-inq-sc-badge-${inq.id}`}
                          className="mt-1 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded text-brand bg-brand-soft border border-brand/30 w-fit"
                        >
                          <GraduationCap size={9} />
                          Camp · {inq._sc_role}
                        </span>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-ink/75 text-sm">
                  <div>{inq.first_name} {inq.last_name}</div>
                  <div className="text-xs text-ink/55">{inq.email}</div>
                </TableCell>
                <TableCell className="text-ink/65 text-xs">{inq.audience || "—"}</TableCell>
                <TableCell className="text-ink/65 text-xs capitalize">{(inq.interest_type || "").replace(/_/g, " ") || "—"}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={STATUS_STYLE[inq.status || "new"]}>
                    {(STATUS_OPTIONS.find((s) => s.value === (inq.status || "new")) || STATUS_OPTIONS[0]).label}
                  </Badge>
                </TableCell>
                <TableCell className="text-ink/65 font-mono-spec text-xs">
                  {inq.follow_up_active ? `${inq.follow_up_step || 0}/4` : "—"}
                </TableCell>
                <TableCell className="text-ink/65 text-xs">
                  {inq.created_at ? new Date(inq.created_at).toLocaleDateString() : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" className="btn-outline-ink rounded-full h-8 px-3 text-xs" onClick={(e) => { e.stopPropagation(); openDetail(inq); }}>View</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-white border border-[var(--line)] text-ink max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="org-inq-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-[-0.025em] flex items-center gap-2">
                  <Building2 size={18} className="text-brand" />
                  {selected.organization_name || "(unnamed organization)"}
                </DialogTitle>
                <DialogDescription className="text-ink/65">
                  Submitted {selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5 pt-2">
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <DetailRow label="Contact" value={`${selected.first_name || ""} ${selected.last_name || ""}`.trim() || "—"} />
                  <DetailRow label="Role" value={selected.role_title} />
                  <DetailRow label="Email" value={selected.email} icon={<Mail size={12} />} link={`mailto:${selected.email}`} />
                  <DetailRow label="Phone" value={selected.phone} icon={<Phone size={12} />} link={selected.phone ? `tel:${selected.phone}` : null} />
                  <DetailRow label="Audience" value={selected.audience} />
                  <DetailRow label="Audience size" value={selected.audience_size} />
                  <DetailRow label="Interest type" value={(selected.interest_type || "").replace(/_/g, " ")} />
                  <DetailRow label="Timing" value={selected.timing} icon={<Calendar size={12} />} />
                </div>

                {selected.message && (
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono-spec mb-1.5">Message</div>
                    <div className="rounded-2xl border border-[var(--line)] bg-bone p-4 text-sm text-ink/80 whitespace-pre-wrap leading-relaxed">
                      {selected.message}
                    </div>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono-spec mb-1.5">Status</div>
                    <Select value={editing.status} onValueChange={(v) => setEditing((s) => ({ ...s, status: v }))}>
                      <SelectTrigger data-testid="org-inq-status-select" className="bg-bone border border-[var(--line)]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-ink/55 mt-1.5">Setting Qualified or Closed stops the auto follow-up sequence.</p>
                  </div>
                  <div className="rounded-2xl border border-[var(--line)] bg-bone p-3">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono-spec mb-1">Auto follow-up</div>
                    <div className="text-sm text-ink/80">
                      {selected.follow_up_active ? `Step ${selected.follow_up_step || 0} of 4` : "Inactive"}
                    </div>
                    {selected.follow_up_next_at && selected.follow_up_active && (
                      <div className="text-xs text-ink/55 mt-1">Next send: {new Date(selected.follow_up_next_at).toLocaleString()}</div>
                    )}
                    {selected.last_follow_up_at && (
                      <div className="text-xs text-ink/55 mt-1">Last sent: {new Date(selected.last_follow_up_at).toLocaleString()}</div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono-spec mb-1.5">Admin notes (internal)</div>
                  <Textarea
                    rows={4}
                    value={editing.admin_notes}
                    onChange={(e) => setEditing((s) => ({ ...s, admin_notes: e.target.value }))}
                    placeholder="Conversation history, fit assessment, proposal details…"
                    data-testid="org-inq-notes"
                    className="bg-bone border border-[var(--line)] text-ink focus-visible:ring-brand focus-visible:ring-offset-0"
                  />
                </div>
              </div>

              <DialogFooter className="pt-4 gap-2">
                <Button variant="outline" onClick={() => setSelected(null)} className="btn-outline-ink rounded-full h-10 px-5">Close</Button>
                <Button
                  onClick={saveDetail}
                  disabled={saving}
                  data-testid="org-inq-save"
                  className="btn-ink rounded-full h-10 px-5"
                >
                  {saving ? <Loader2 className="animate-spin" size={13} /> : <><Save size={13} className="mr-1.5" /> Save</>}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({ label, value, icon, link }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono-spec mb-0.5">{label}</div>
      <div className="flex items-center gap-1.5 text-ink">
        {icon && <span className="text-ink/40">{icon}</span>}
        {link ? (
          <a href={link} className="text-brand underline hover:no-underline">{value || "—"}</a>
        ) : (
          <span className="text-sm">{value || "—"}</span>
        )}
      </div>
    </div>
  );
}
